import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { useDeleteVideoPrivateCommentReaction } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReaction';
import { useDeleteVideoPrivateCommentReplyReaction } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReplyReaction';

import { usePrivateComments } from './use-get-comments';
import { getReviewPasswordHashFromCookie, idFromUri } from '../../utils/helper';

export function useDeleteCommentReaction(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  filterOptions?: FilterOptions,
  reviewId?: string,
) {
  const useHook = isPublic ? useDeletePublicCommentReaction : useDeletePrivateCommentReaction;
  return useHook(clipRequestId, sort, filterOptions, reviewId);
}

/**
 * Dummy hook for public comments to follow same pattern as other comment hooks and skip initializing get private comments
 * hook when public comments are being viewed — reactions are not supported on public (viewer) comments as of now.
 */
function useDeletePublicCommentReaction(
  _clipRequestId: string,
  _sort?: Sort,
  _filterOptions?: FilterOptions,
  _reviewId?: string,
) {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const removeReaction = async (_c: string, _r: string, _i?: boolean, _p?: string) => {};
  return { removeReaction };
}

/**
 * Hook to remove emoji reactions from private (collaborator) comments and replies.
 * Performs an optimistic SWR cache update before the API call for instant UI feedback.
 */
function useDeletePrivateCommentReaction(
  clipRequestId: string,
  sort?: Sort,
  filterOptions?: FilterOptions,
  reviewId?: string,
) {
  const [deleteCommentReaction] = useDeleteVideoPrivateCommentReaction();
  const [deleteReplyReaction] = useDeleteVideoPrivateCommentReplyReaction();
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const password = getReviewPasswordHashFromCookie(reviewId);

  const removeReaction = async (
    commentId: string,
    reactionUri: string,
    isReply?: boolean,
    parentCommentId?: string,
  ) => {
    const reactionId = idFromUri(reactionUri) as unknown as number;

    // Optimistic update: immediately remove the reaction from the SWR cache
    let deletedReaction: Record<string, any> | undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getOptimisticData = (pages: any, action: 'remove' | 'add') => {
      if (!pages) return pages;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return pages.map((page: any) => ({
        ...page,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: page.data.map((comment: any) => {
          if (isReply && parentCommentId && comment.uri === parentCommentId) {
            return {
              ...comment,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              replies: comment.replies?.map((reply: any) => {
                if (reply.uri !== commentId) return reply;
                if (!deletedReaction) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  deletedReaction = reply.reactions?.find((r: any) => r.uri === reactionUri);
                }
                return {
                  ...reply,
                  reactions:
                    action === 'remove'
                      ? (reply.reactions?.filter((r: any) => r.uri !== reactionUri) ?? [])
                      : deletedReaction
                        ? [...(reply.reactions ?? []), deletedReaction]
                        : (reply.reactions ?? []),
                };
              }),
            };
          }
          if (!isReply && comment.uri === commentId) {
            if (!deletedReaction) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              deletedReaction = comment.reactions?.find((r: any) => r.uri === reactionUri);
            }
            return {
              ...comment,
              reactions:
                action === 'remove'
                  ? (comment.reactions?.filter((r: any) => r.uri !== reactionUri) ?? [])
                  : deletedReaction
                    ? [...(comment.reactions ?? []), deletedReaction]
                    : (comment.reactions ?? []),
            };
          }
          return comment;
        }),
      }));
    };

    revalidateComments(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (pages: any) => getOptimisticData(pages, 'remove'),
      { revalidate: false },
    );

    try {
      if (isReply && parentCommentId) {
        await deleteReplyReaction({
          where: {
            videoId: clipRequestId as unknown as number,
            commentId: idFromUri(parentCommentId) as unknown as number,
            replyId: idFromUri(commentId) as unknown as number,
            reactionId,
          },
          ...(reviewId || password ? { query: { reviewId, password } } : {}),
        });
      } else {
        await deleteCommentReaction({
          where: {
            videoId: clipRequestId as unknown as number,
            commentId: idFromUri(commentId) as unknown as number,
            reactionId,
          },
          ...(reviewId || password ? { query: { reviewId, password } } : {}),
        });
      }
    } catch {
      // Revert optimistic update on failure
      revalidateComments(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (pages: any) => getOptimisticData(pages, 'add'),
        { revalidate: false },
      );
    } finally {
      revalidateComments();
    }
  };

  return { removeReaction };
}
